import type { Cell, CoverRequest } from "./types";
import { withinRadius } from "./geometry";

/** Precomputed coverage structure for decoding a solved model. */
export interface CoverageModel {
  lp: string;
  /** Candidate cells, indexed by the y-variable index. */
  candidates: Cell[];
  /** Demand cells, indexed by the z-variable index. */
  demands: Cell[];
  /** demand index -> candidate indices that cover it. */
  coverage: number[][];
}

/**
 * Build the Maximal Covering Location Problem as a 0/1 mixed-integer linear
 * program in CPLEX LP format (the format HiGHS reads):
 *
 *   maximize    Σ z_i                      (number of palms watered)
 *   subject to  z_i ≤ Σ_{j covers i} y_j    (a palm is covered only by a chosen noria)
 *               Σ_j y_j ≤ p                 (a limited number of norias)
 *               y_j ∈ {0, 1}                (place a noria on candidate cell j, or not)
 *               0 ≤ z_i ≤ 1                 (z_i is integral at the optimum, so leave it continuous)
 *
 * Keeping z continuous (only y binary) shrinks the integer search without
 * changing the optimum: maximizing pushes each z_i to min(1, Σ y_j), which is
 * integral because y is binary.
 */
export function buildCoverageLP(req: CoverRequest): CoverageModel {
  const { radius, facilities, demands, candidates } = req;

  // For each demand point, the candidate sites whose radius reaches it.
  const coverage: number[][] = demands.map(([dr, dc]) => {
    const sites: number[] = [];
    for (let j = 0; j < candidates.length; j++) {
      const [cr, cc] = candidates[j];
      if (withinRadius(cr, cc, dr, dc, radius)) sites.push(j);
    }
    return sites;
  });

  let lp = "\\ Maximal Covering Location Problem (MCLP) - HiGHS MILP\n";
  lp += "\\ " + demands.length + " palms, " + candidates.length + " candidate sites, "
    + facilities + " norias, radius " + radius + "\n";

  // Objective: maximize the number of covered palms. Guard the degenerate
  // empty-demand case so the LP still parses.
  const objTerms = demands.map((_, i) => `z${i}`);
  lp += "Maximize\n obj: " + (objTerms.length ? objTerms.join(" + ") : "0") + "\n";

  lp += "Subject To\n";
  for (let i = 0; i < demands.length; i++) {
    const sites = coverage[i];
    if (sites.length > 0) {
      lp += ` cov_${i}: z${i} - ${sites.map((j) => `y${j}`).join(" - ")} <= 0\n`;
    } else {
      // No candidate can reach this palm: it can never be covered.
      lp += ` cov_${i}: z${i} <= 0\n`;
    }
  }
  if (candidates.length > 0) {
    lp += ` budget: ${candidates.map((_, j) => `y${j}`).join(" + ")} <= ${facilities}\n`;
  }

  lp += "Bounds\n";
  for (let i = 0; i < demands.length; i++) lp += ` 0 <= z${i} <= 1\n`;
  for (let j = 0; j < candidates.length; j++) lp += ` 0 <= y${j} <= 1\n`;

  if (candidates.length > 0) {
    lp += "Binary\n";
    for (let j = 0; j < candidates.length; j++) lp += ` y${j}\n`;
  }
  lp += "End\n";

  return { lp, candidates, demands, coverage };
}
